/**
 * Prepares reCAPTCHA Enterprise before Firebase App Check / Auth run.
 */
import { environment } from './environments/environment';

declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string;
    grecaptcha?: {
      enterprise?: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, opts: { action: string }) => Promise<string>;
      };
    };
  }
}

export function prepareAppCheck(): Promise<void> {
  const fixedDebug = environment.appCheckDebugToken?.trim();
  if (fixedDebug) {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = fixedDebug;
    return Promise.resolve();
  }
  if (environment.firebaseAppCheckDebug) {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    return Promise.resolve();
  }
  return waitForRecaptchaEnterprise();
}

/** Ensures enterprise.js is loaded (call again before login if needed). */
export function waitForRecaptchaEnterprise(): Promise<void> {
  if (environment.appCheckDebugToken?.trim() || environment.firebaseAppCheckDebug) {
    return Promise.resolve();
  }

  const siteKey = environment.appCheckRecaptchaSiteKey?.trim();
  if (!siteKey || siteKey.includes('REPLACE')) {
    console.warn('[App Check] Site key missing — run npm run env:sync');
    return Promise.resolve();
  }

  return loadRecaptchaEnterprise(siteKey);
}

function loadRecaptchaEnterprise(siteKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const onReady = () => {
      if (window.grecaptcha?.enterprise) {
        window.grecaptcha.enterprise.ready(() => resolve());
      } else {
        resolve();
      }
    };

    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="recaptcha/enterprise.js"]',
    );
    if (existing) {
      if (window.grecaptcha?.enterprise) {
        onReady();
      } else {
        existing.addEventListener('load', onReady, { once: true });
        existing.addEventListener('error', () => reject(loadError()), { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = onReady;
    script.onerror = () => reject(loadError());
    document.head.appendChild(script);
  });
}

function loadError(): Error {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  return new Error(
    `No se pudo cargar reCAPTCHA Enterprise en "${host}". Añade ese dominio en la site key (Google Cloud → reCAPTCHA).`,
  );
}
